import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import StopIcon from '@mui/icons-material/Stop';
import CloseIcon from '@mui/icons-material/Close';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import CircularProgress from '@mui/material/CircularProgress';
import { holdSessionAwake } from '../../services/sessionManager';

/**
 * VoiceLivePanel — Real-time voice conversation interface.
 * Replaces ChatInput when voice mode is active.
 */
const VoiceLivePanel = forwardRef(({ socket, conversationId, onClose, eventPrefix = '' }, ref) => {
  // Event names — configurable prefix for training namespace ('training:voice:' vs 'client:voice:')
  const events = {
    startSession: eventPrefix ? `${eventPrefix}start_session` : 'client:voice:start_session',
    audioChunk: eventPrefix ? `${eventPrefix}audio_chunk` : 'client:voice:audio_chunk',
    endSession: eventPrefix ? `${eventPrefix}end_session` : 'client:voice:end_session',
    interrupt: eventPrefix ? `${eventPrefix}interrupt` : 'client:voice:interrupt',
    sessionStarted: eventPrefix ? `${eventPrefix}session_started` : 'server:voice:session_started',
    audioResponse: eventPrefix ? `${eventPrefix}patient_audio` : 'server:voice:audio_chunk',
    studentTranscription: eventPrefix ? `${eventPrefix}student_transcription` : 'server:voice:transcription',
    patientTranscription: eventPrefix ? `${eventPrefix}patient_transcription` : 'server:voice:ai_transcription',
    turnComplete: eventPrefix ? `${eventPrefix}turn_complete` : 'server:voice:turn_complete',
    sessionEnded: eventPrefix ? `${eventPrefix}session_ended` : 'server:voice:session_ended',
    error: eventPrefix ? `${eventPrefix}error` : 'server:voice:error',
  };
  const [state, setState] = useState('idle'); // idle | connecting | listening | speaking | paused | error
  // A voice session keeps the web session awake without keyboard or mouse input (security batch 2).
  useEffect(() => (state !== 'idle' && state !== 'error' ? holdSessionAwake('voice_live') : undefined), [state]);
  const [error, setError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [aiText, setAiText] = useState('');
  const [userText, setUserText] = useState('');

  // Refs for values needed in audio callbacks (avoid stale closures)
  const stateRef = useRef('idle');
  const isMutedRef = useRef(false);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const audioContextRef = useRef(null);
  const playbackContextRef = useRef(null);
  const playbackQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  // True once the server signals turn_complete for the current AI turn. The mic
  // must stay gated (state 'speaking') until BOTH the turn is complete AND the
  // playback queue has drained — otherwise the mic re-opens while the AI's own
  // audio tail is still playing through the speakers and the live session hears
  // itself (cause of the reported reverb/echo AND role-reversal in AV mode).
  const turnCompleteRef = useRef(false);
  const mountedRef = useRef(true);
  const hasStartedRef = useRef(false);
  const socketRef = useRef(socket);
  const conversationIdRef = useRef(conversationId);
  const pausedRef = useRef(false);

  // Expose pause/resume/end to parent (ChatInterface) via ref
  useImperativeHandle(ref, () => ({
    pause: () => {
      console.log('[VoiceLive] Pausing voice session');
      pausedRef.current = true;
      isMutedRef.current = true;
      setIsMuted(true);
      // Clear playback queue and stop audio
      playbackQueueRef.current = [];
      isPlayingRef.current = false;
      if (playbackContextRef.current && playbackContextRef.current.state !== 'closed') {
        playbackContextRef.current.close().catch(() => {});
        playbackContextRef.current = null;
      }
      setState('paused');
    },
    resume: () => {
      console.log('[VoiceLive] Resuming voice session');
      pausedRef.current = false;
      isMutedRef.current = false;
      setIsMuted(false);
      // Re-create playback context
      try {
        playbackContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
      } catch (e) {}
      setState('listening');
    },
    end: () => {
      handleEndSession();
    },
    isActive: () => state !== 'idle' && state !== 'error',
  }));

  // Keep refs in sync
  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { isMutedRef.current = isMuted; }, [isMuted]);
  useEffect(() => { socketRef.current = socket; }, [socket]);
  useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopRecording();
      if (socketRef.current && conversationIdRef.current) {
        socketRef.current.emit(events.endSession, { sessionId: conversationIdRef.current, conversationId: conversationIdRef.current });
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopRecording = useCallback(() => {
    if (processorRef.current) {
      try { processorRef.current.disconnect(); } catch (e) {}
      processorRef.current = null;
    }
    if (audioContextRef.current) {
      try { audioContextRef.current.close(); } catch (e) {}
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
  }, []);

  const startRecording = useCallback(() => {
    if (!mediaStreamRef.current) {
      console.error('[VoiceLive] No media stream for recording');
      return;
    }

    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(mediaStreamRef.current);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e) => {
        // Defensive: this capture node is connected to destination (required for
        // the ScriptProcessor to fire in some browsers), so explicitly silence
        // its output every callback — the mic must NEVER be routed to the
        // speakers, which would feed back as echo. Do this BEFORE the early
        // return so it also applies while muted / not listening.
        if (e.outputBuffer && typeof e.outputBuffer.getChannelData === 'function') {
          e.outputBuffer.getChannelData(0).fill(0);
        }
        // Use refs to avoid stale closures
        if (isMutedRef.current || stateRef.current !== 'listening') return;

        const inputData = e.inputBuffer.getChannelData(0);
        const pcm16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }

        // Convert to base64 without spreading large arrays
        const bytes = new Uint8Array(pcm16.buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = btoa(binary);

        const sock = socketRef.current;
        const convId = conversationIdRef.current;
        if (sock && convId) {
          sock.emit(events.audioChunk, { sessionId: convId, conversationId: convId, audioData: base64 });
        }
      };

      source.connect(processor);
      processor.connect(audioContext.destination);
      console.log('[VoiceLive] Recording started');
    } catch (err) {
      console.error('[VoiceLive] Recording setup error:', err);
      if (mountedRef.current) setError('Failed to start audio recording: ' + err.message);
    }
  }, []);

  // Use a ref for the playback function to avoid stale closure issues
  const playNextChunkRef = useRef(null);

  const playAudioChunk = useCallback((audioData) => {
    if (!audioData) return;
    try {
      const binaryString = atob(audioData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      playbackQueueRef.current.push(bytes.buffer);
      console.log('[VoiceLive] Audio chunk queued, queue size:', playbackQueueRef.current.length, 'bytes:', bytes.length);
      if (!isPlayingRef.current && playNextChunkRef.current) {
        playNextChunkRef.current();
      }
    } catch (err) {
      console.error('[VoiceLive] Audio decode error:', err);
    }
  }, []);

  // Define playNextChunk and store in ref for self-referencing
  useEffect(() => {
    playNextChunkRef.current = async () => {
      if (playbackQueueRef.current.length === 0) {
        isPlayingRef.current = false;
        // Playback has drained. If the AI's turn is also complete, NOW it's safe
        // to re-open the mic — the speakers are quiet, so the mic won't capture
        // the AI's own voice (the reverb/echo + role-reversal fix).
        if (turnCompleteRef.current && mountedRef.current && !pausedRef.current) {
          turnCompleteRef.current = false;
          stateRef.current = 'listening';
          setState('listening');
        }
        return;
      }
      isPlayingRef.current = true;
      const audioBuffer = playbackQueueRef.current.shift();

      try {
        if (!playbackContextRef.current || playbackContextRef.current.state === 'closed') {
          playbackContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
        }
        const ctx = playbackContextRef.current;

        // Resume AudioContext if suspended (browser autoplay policy)
        if (ctx.state === 'suspended') {
          console.log('[VoiceLive] Resuming suspended AudioContext');
          await ctx.resume();
        }

        const pcm16 = new Int16Array(audioBuffer);
        const float32 = new Float32Array(pcm16.length);
        for (let i = 0; i < pcm16.length; i++) {
          float32[i] = pcm16[i] / 32768;
        }

        console.log('[VoiceLive] Playing audio chunk, samples:', float32.length, 'ctx state:', ctx.state);

        const buffer = ctx.createBuffer(1, float32.length, 24000);
        buffer.getChannelData(0).set(float32);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.onended = () => {
          if (playNextChunkRef.current) playNextChunkRef.current();
        };
        source.start();
      } catch (err) {
        console.error('[VoiceLive] Playback error:', err);
        isPlayingRef.current = false;
        // Re-enter playNextChunk unconditionally: if more chunks remain it plays
        // the next one; if the queue is now empty its drain branch re-opens the
        // mic (so a failed LAST chunk can't leave the mic stuck gated).
        if (playNextChunkRef.current) {
          playNextChunkRef.current();
        }
      }
    };
  }, []);

  // Socket event listeners
  useEffect(() => {
    if (!socket) return;

    const handleSessionStarted = (data) => {
      if (!mountedRef.current) return;
      console.log('[VoiceLive] Session started:', data.sessionId);
      setState('listening');
      startRecording();
    };

    const handleAudioResponse = (data) => {
      if (!mountedRef.current || pausedRef.current) return;
      // A new/continuing AI turn — gate the mic immediately. Set stateRef
      // synchronously (not just via setState, which only lands on the next
      // render) so onaudioprocess stops sending mic audio this instant.
      turnCompleteRef.current = false;
      stateRef.current = 'speaking';
      setState('speaking');
      playAudioChunk(data.audioData);
    };

    const handleTranscription = (data) => {
      if (!mountedRef.current) return;
      setUserText(data.text);
    };

    const handleAiTranscription = (data) => {
      if (!mountedRef.current) return;
      setAiText(data.text);
    };

    const handleTurnComplete = () => {
      if (!mountedRef.current) return;
      setAiText('');
      // The server's turn is done, but the AI audio may still be playing out of
      // the queue. Only re-open the mic once playback has actually drained;
      // otherwise defer to playNextChunk's drain branch (below).
      turnCompleteRef.current = true;
      if (playbackQueueRef.current.length === 0 && !isPlayingRef.current) {
        stateRef.current = 'listening';
        setState('listening');
      }
    };

    const handleSessionEnded = () => {
      if (!mountedRef.current) return;
      console.log('[VoiceLive] Session ended by server');
      setState('idle');
      stopRecording();
    };

    const handleError = (data) => {
      if (!mountedRef.current) return;
      console.error('[VoiceLive] Server error:', data);
      setError(data.error || data.reason || 'Voice session error');
    };

    socket.on(events.sessionStarted, handleSessionStarted);
    socket.on(events.audioResponse, handleAudioResponse);
    socket.on(events.studentTranscription, handleTranscription);
    socket.on(events.patientTranscription, handleAiTranscription);
    socket.on(events.turnComplete, handleTurnComplete);
    socket.on(events.sessionEnded, handleSessionEnded);
    socket.on(events.error, handleError);

    return () => {
      socket.off(events.sessionStarted, handleSessionStarted);
      socket.off(events.audioResponse, handleAudioResponse);
      socket.off(events.studentTranscription, handleTranscription);
      socket.off(events.patientTranscription, handleAiTranscription);
      socket.off(events.turnComplete, handleTurnComplete);
      socket.off(events.sessionEnded, handleSessionEnded);
      socket.off(events.error, handleError);
    };
  }, [socket, startRecording, stopRecording, playAudioChunk]);

  // Auto-start session once on mount
  useEffect(() => {
    if (hasStartedRef.current || !socket || !conversationId) return;
    hasStartedRef.current = true;

    const startSession = async () => {
      setState('connecting');
      setError(null);

      // Request microphone permission
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          }
        });
        mediaStreamRef.current = stream;
      } catch (err) {
        console.error('[VoiceLive] Microphone access denied:', err);
        setError('Microphone access is required for voice mode');
        setState('error');
        return;
      }

      // Pre-create playback AudioContext during user gesture context
      // This ensures it won't be blocked by autoplay policy
      try {
        playbackContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
        console.log('[VoiceLive] Playback AudioContext created, state:', playbackContextRef.current.state);
        if (playbackContextRef.current.state === 'suspended') {
          await playbackContextRef.current.resume();
          console.log('[VoiceLive] Playback AudioContext resumed');
        }
      } catch (err) {
        console.warn('[VoiceLive] Failed to pre-create playback context:', err);
      }

      // Request session via socket
      console.log('[VoiceLive] Requesting session for conversation:', conversationId);
      socket.emit(events.startSession, { sessionId: conversationId, conversationId });
    };

    startSession();
  }, [socket, conversationId]);

  const handleInterrupt = useCallback(() => {
    if (socketRef.current && conversationIdRef.current) {
      socketRef.current.emit(events.interrupt, { sessionId: conversationIdRef.current, conversationId: conversationIdRef.current });
    }
    playbackQueueRef.current = [];
    isPlayingRef.current = false;
    if (playbackContextRef.current) {
      try { playbackContextRef.current.close(); } catch (e) {}
      playbackContextRef.current = null;
    }
    setState('listening');
  }, []);

  const handleEndSession = useCallback(() => {
    if (socketRef.current && conversationIdRef.current) {
      socketRef.current.emit(events.endSession, { sessionId: conversationIdRef.current, conversationId: conversationIdRef.current });
    }
    stopRecording();
    playbackQueueRef.current = [];
    isPlayingRef.current = false;
    setState('idle');
    if (onClose) onClose();
  }, [stopRecording, onClose]);

  const getStatusText = () => {
    switch (state) {
      case 'connecting': return 'Connecting to voice mode...';
      case 'listening': return isMuted ? 'Microphone muted' : 'Listening — speak now...';
      case 'speaking': return 'AI is speaking...';
      case 'paused': return 'Voice paused';
      case 'error': return error || 'Error occurred';
      default: return 'Voice mode';
    }
  };

  const getStatusColor = () => {
    switch (state) {
      case 'listening': return isMuted ? 'text.secondary' : '#4caf50';
      case 'speaking': return '#2196f3';
      case 'paused': return '#ff9800';
      case 'error': return '#f44336';
      case 'connecting': return '#ff9800';
      default: return 'text.secondary';
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        p: 3,
        bgcolor: '#f5f0ff',
        borderTop: '2px solid',
        borderColor: 'secondary.main',
        gap: 1.5,
        position: 'relative',
        minHeight: 140,
      }}
    >
      {/* Close button */}
      <Tooltip title="End voice session">
        <IconButton
          onClick={handleEndSession}
          size="small"
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {/* Status */}
      <Typography variant="body2" sx={{ color: getStatusColor(), fontWeight: 600 }}>
        {getStatusText()}
      </Typography>

      {/* Transcription display */}
      {(userText || aiText) && (
        <Box sx={{ width: '100%', maxHeight: 48, overflow: 'hidden', textAlign: 'center' }}>
          {aiText && (
            <Typography variant="body2" color="primary" noWrap sx={{ fontStyle: 'italic' }}>
              {aiText}
            </Typography>
          )}
          {userText && !aiText && (
            <Typography variant="body2" color="text.secondary" noWrap>
              You: {userText}
            </Typography>
          )}
        </Box>
      )}

      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {/* Mute toggle */}
        {(state === 'listening' || state === 'speaking') && (
          <Tooltip title={isMuted ? 'Unmute microphone' : 'Mute microphone'}>
            <IconButton
              onClick={() => setIsMuted(prev => !prev)}
              sx={{
                bgcolor: isMuted ? 'error.light' : 'action.hover',
                color: isMuted ? 'white' : 'inherit',
                '&:hover': { bgcolor: isMuted ? 'error.main' : 'action.selected' },
              }}
              size="medium"
            >
              {isMuted ? <MicOffIcon /> : <MicIcon />}
            </IconButton>
          </Tooltip>
        )}

        {/* Main indicator / action button */}
        {state === 'connecting' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <CircularProgress size={48} color="secondary" />
          </Box>
        )}

        {state === 'speaking' && (
          <Tooltip title="Interrupt — stop AI and speak">
            <IconButton
              onClick={handleInterrupt}
              sx={{
                width: 64,
                height: 64,
                bgcolor: '#2196f3',
                color: 'white',
                '&:hover': { bgcolor: '#1976d2' },
                animation: 'voicePulse 1.5s ease-in-out infinite',
                '@keyframes voicePulse': {
                  '0%, 100%': { boxShadow: '0 0 0 0 rgba(33, 150, 243, 0.4)' },
                  '50%': { boxShadow: '0 0 0 14px rgba(33, 150, 243, 0)' },
                },
              }}
            >
              <GraphicEqIcon fontSize="large" />
            </IconButton>
          </Tooltip>
        )}

        {state === 'listening' && (
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: isMuted ? 'grey.400' : '#4caf50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.3s',
              animation: isMuted ? 'none' : 'micPulse 2s ease-in-out infinite',
              '@keyframes micPulse': {
                '0%, 100%': { boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.4)' },
                '50%': { boxShadow: '0 0 0 14px rgba(76, 175, 80, 0)' },
              },
            }}
          >
            <MicIcon sx={{ color: 'white', fontSize: 32 }} />
          </Box>
        )}

        {state === 'error' && (
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              bgcolor: 'error.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <MicOffIcon sx={{ color: 'white', fontSize: 32 }} />
          </Box>
        )}

        {/* End session button */}
        {state !== 'idle' && state !== 'connecting' && (
          <Tooltip title="End voice session">
            <IconButton
              onClick={handleEndSession}
              sx={{
                bgcolor: 'error.light',
                color: 'white',
                '&:hover': { bgcolor: 'error.main' },
              }}
              size="medium"
            >
              <StopIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Error detail */}
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5, textAlign: 'center', maxWidth: '80%' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
});

VoiceLivePanel.displayName = 'VoiceLivePanel';

export default VoiceLivePanel;
